import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { selectActiveTool } from "@/store/ui/ui-selectors";
import { deactivateTool, ToolName } from "@/store/ui/ui-slice";
import { selectHasWritePermission } from "@/store/user-selectors";
import {
  cyan,
  FaroIconButton,
  FaroMenu,
  FaroMenuItem,
  FaroTooltip,
  grey,
  red,
  ThreeDotsIcon,
} from "@faro-lotv/flat-ui";
import { IElement } from "@faro-lotv/ielement-types";
import { useRef, useState, type JSX } from "react";

export type AnalysisViewerMenuProps = {
  /** if true toolbar and menu are in edit mode */
  editMode: boolean;

  /** True if the analysis has unsaved changes */
  isAnalysisDirty: boolean;

  /** The IElement of the analysis, if available */
  analysisElement?: IElement;

  /** Callback when the user clicks on the save button */
  onSave(): void;

  /** Callback when the user clicks on the share button */
  onShare(): void;

  /** Callback when the user clicks on the Create Report button */
  onCreateReport(): void;

  /** Callback when the user clicks on the delete button */
  onDelete(): void;
};

const menuHoverStyle = {
  ":hover *": {
    color: `${cyan[400]} !important`,
  },
};

/**
 * @returns Three dots menu allowing user to trigger different actions (editing, deleting, etc.)
 */
export function AnalysisViewerMenu({
  editMode,
  isAnalysisDirty,
  analysisElement,
  onSave,
  onShare,
  onCreateReport,
  onDelete,
}: AnalysisViewerMenuProps): JSX.Element {
  const dispatch = useAppDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const activeTool = useAppSelector(selectActiveTool);

  // If the user has permission to edit the project or not
  const hasWritePermission = useAppSelector(selectHasWritePermission);

  return (
    <>
      <FaroIconButton
        size="s"
        margin={0}
        color={grey[0]}
        hoverColor={cyan[400]}
        ref={button}
        aria-label="analysis toolbar menu"
        onClick={(e) => {
          e.stopPropagation();

          // when open the menu deactivate the analysis label tool if it's active
          // to hide temporary labels
          if (activeTool === ToolName.analysisLabel) {
            dispatch(deactivateTool());
          }

          setIsOpen(true);
        }}
      >
        <ThreeDotsIcon />
      </FaroIconButton>
      <FaroMenu
        minWidth={180}
        dark
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: 120 }}
        anchorEl={button.current}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        autoFocus={false}
      >
        {!editMode && (
          <FaroTooltip
            title={
              hasWritePermission || !isAnalysisDirty
                ? ""
                : "Saving point cloud analysis is only accessible with edit permissions"
            }
          >
            <FaroMenuItem
              onClick={() => {
                if (isAnalysisDirty) {
                  onSave();
                } else {
                  onShare();
                }

                setIsOpen(false);
              }}
              disabled={!hasWritePermission && isAnalysisDirty}
              color={grey[0]}
              label={isAnalysisDirty ? "Save" : "Share"}
              sx={menuHoverStyle}
            />
          </FaroTooltip>
        )}

        <FaroMenuItem
          onClick={() => {
            setIsOpen(false);
            // defer report creation to provide immediate close of menu
            setTimeout(() => {
              onCreateReport();
            }, 0);
          }}
          color={grey[0]}
          label="Create report"
          disabled={!analysisElement}
          sx={menuHoverStyle}
        />

        <FaroTooltip
          title={
            hasWritePermission || !analysisElement
              ? ""
              : "Delete point cloud analysis is only accessible with edit permissions"
          }
        >
          <FaroMenuItem
            onClick={() => {
              onDelete();
              setIsOpen(false);
            }}
            color={red[300]}
            highlightColor={red[300]}
            label="Delete"
            disabled={analysisElement && !hasWritePermission}
          />
        </FaroTooltip>
      </FaroMenu>
    </>
  );
}
